import React, { useEffect } from 'react';
import './HomePage.css';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SecurityIcon from '@mui/icons-material/Security';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShieldIcon from '@mui/icons-material/Shield';
import SpeedIcon from '@mui/icons-material/Speed';

export default function HomePage() {
  useEffect(() => {
    // Animation au scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observer tous les éléments avec la classe animate-on-scroll
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="hp-root">
      {/* NAVBAR */}
      <nav className="hp-nav">
        <img src="/logo.jpg" alt="Spayis logo" className="hp-logo animate-fade-in" style={{ height: '40px', width: 'auto' }} />
        <div className="hp-nav-menu">
          <a href="#fonctionnement" className="nav-link-animate">Fonctionnement</a>
          <a href="#pricing" className="nav-link-animate">Tarifs</a>
          <a href="#securite" className="nav-link-animate">Sécurité</a>
        </div>
        <div className="hp-nav-actions">
          <button className="btn btn-link btn-hover-animate">Connexion</button>
          <button className="btn btn-dark btn-hover-animate">S'inscrire</button>
        </div>
      </nav>

      {/* HERO */}
      <header className="hp-hero">
        <div className="hp-hero-left animate-on-scroll" data-animation="fade-in-up">
          <span className="hp-badge">✨ VIVEZ LA LIBERTÉ SANS LIMITES ✨</span>
          <h1 className="hp-title">
            <span className="hp-title-main">SHOP STRESS-FREE.</span>
            <span className="hp-title-secondary">PAY WITHOUT</span>
            <span className="hp-title-secondary">PRESSURE.</span>
          </h1>
          <p className="hp-lead">Experience the freedom of split payments. Join millions of happy shoppers across the continent and beyond who shop with total confidence.</p>
          <div className="hp-cta">
            <button className="btn btn-dark large btn-pulse">Start Shopping</button>
            <button className="btn btn-outline btn-hover-animate">For Businesses</button>
          </div>
          <div className="hp-social-proof">
            <div className="proof-avatars">
              <div className="avatar">
                <img src="/avatar1.png" alt="user" />
              </div>
              <div className="avatar">
                <img src="/avatar2.png" alt="user" />
              </div>
              <div className="avatar">
                <img src="/avatar3.png" alt="user" />
              </div>
              <div className="avatar">
                <img src="/avatar4.png" alt="user" />
              </div>
            </div>
            <span className="proof-text">HAPPY USERS TRUST</span>
          </div>
        </div>
        <div className="hp-hero-right animate-on-scroll" data-animation="fade-in-right">
          <img src="/hero.png" alt="shopping" className="hero-img img-zoom" />
          <div className="payment-card card-float">
            <div className="payment-badge">Payment Successful</div>
            <div className="payment-amount">200.000XAF</div>
            <div className="payment-info">Premium Sneakers · 4 × 50.000XAF</div>
          </div>
        </div>
      </header>

      {/* HOW IT WORKS */}
      <section className="hp-how" id="fonctionnement">
        <div className="hp-how-content">
          <div className="hp-section-header animate-on-scroll" data-animation="fade-in">
            <h2>HOW IT WORKS.</h2>
            <p style={{color: 'white'}}>We removed the friction. No long forms. No hard credit checks. Just seamless shopping at your fingertips.</p>
          </div>
          <div className="hp-how-grid">
            <div className="hp-how-item animate-on-scroll" data-animation="fade-in-up">
              <div className="hp-icon-box icon-float">
                <ShoppingBagIcon className="hp-icon" />
              </div>
              <h4>SHOP</h4>
              <p style={{color: 'white'}}>Add any item to your cart</p>
            </div>
            <div className="hp-how-item animate-on-scroll" data-animation="fade-in-up">
              <div className="hp-icon-box icon-float">
                <SplitscreenIcon className="hp-icon" />
              </div>
              <h4>SPLIT</h4>
              <p style={{color: 'white'}}>Split into fixed payment</p>
            </div>
            <div className="hp-how-item animate-on-scroll" data-animation="fade-in-up">
              <div className="hp-icon-box icon-float">
                <CheckCircleIcon className="hp-icon" />
              </div>
              <h4>PAY</h4>
              <p style={{color: 'white'}}>Pay without pressure</p>
            </div>
            <div className="hp-how-item animate-on-scroll" data-animation="fade-in-up">
              <div className="hp-icon-box icon-float">
                <FavoriteBorderIcon className="hp-icon" />
              </div>
              <h4>ENJOY</h4>
              <p style={{color: 'white'}}>Enjoy your purchase, guilt-free</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="hp-pricing" id="pricing">
        <div className="animate-on-scroll" data-animation="fade-in">
          <h2>Votre plan de paiement</h2>
          <p className="hp-section-subtitle">Sur une commande de 200.000 XAF à effectuer aujourd'hui</p>
        </div>
        <div className="hp-pricing-grid">
          <div className="hp-pricing-card active animate-on-scroll" data-animation="fade-in-up">
            <div className="hp-pricing-label">Aujourd'hui</div>
            <div className="hp-pricing-amount">50.000 XAF</div>
            <div className="hp-pricing-desc">1ère part</div>
          </div>
          <div className="hp-pricing-card animate-on-scroll" data-animation="fade-in-up">
            <div className="hp-pricing-label">Dans 3 semaines</div>
            <div className="hp-pricing-amount">50.000 XAF</div>
            <div className="hp-pricing-desc">2e part</div>
          </div>
          <div className="hp-pricing-card animate-on-scroll" data-animation="fade-in-up">
            <div className="hp-pricing-label">Dans 4 semaines</div>
            <div className="hp-pricing-amount">50.000 XAF</div>
            <div className="hp-pricing-desc">3e part</div>
          </div>
          <div className="hp-pricing-card animate-on-scroll" data-animation="fade-in-up">
            <div className="hp-pricing-label">Dans 4 semaines</div>
            <div className="hp-pricing-amount">50.000 XAF</div>
            <div className="hp-pricing-desc">4e part</div>
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="hp-security">
        <div className="hp-security-content animate-on-scroll" data-animation="fade-in-up">
          <SecurityIcon className="hp-security-icon icon-bounce" />
          <h3>Protection et sécurité</h3>
          <p>Vos données bancaires sont chiffrées et nos facturations passent par les normes de sécurité les plus strictes. Vous pouvez faire confiance à Spays pour gérer vos paiements.</p>
          <button className="btn btn-dark btn-hover-animate">Demander maintenant</button>
        </div>
      </section>

      {/* BOOST */}
      <section className="hp-boost">
        <div className="hp-boost-container">
          <div className="hp-boost-left animate-on-scroll" data-animation="fade-in-left">
            <h2>Boostez votre panier moyen de <span className="boost-accent">+45%</span>.</h2>
            <p>Offer a customer the liberty to buy what they're interested in, integrate Spayis in minutes and commence to vend plus. sans risque de fonds de non-paiement.</p>
            <ul className="hp-boost-features">
              <li>
                <SpeedIcon />
                <span>Installation éclair API & Intégration</span>
              </li>
              <li>
                <StorefrontIcon />
                <span>Vous êtes payé sous 24h</span>
              </li>
              <li>
                <ShieldIcon />
                <span>Support client 24/7</span>
              </li>
              <li>
                <CheckCircleIcon />
                <span>Augmentation du taux de conversion</span>
              </li>
            </ul>
            <button className="btn btn-secondary btn-hover-animate">Devenir partenaire</button>
          </div>
          <div className="hp-boost-right animate-on-scroll" data-animation="fade-in-right">
            <div className="hp-stat stat-scale">
              <div className="hp-stat-value">1.8x</div>
              <div className="hp-stat-label">Plus de ventes</div>
            </div>
            <div className="hp-stat stat-scale">
              <div className="hp-stat-value">+25k</div>
              <div className="hp-stat-label">Interactions</div>
            </div>
            <div className="hp-stat hp-stat-highlight stat-scale">
              <div className="hp-stat-value">0%</div>
              <div className="hp-stat-label">Taux d'abandon</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="hp-footer">
        <div className="hp-footer-content">
          <div className="hp-footer-col">

            <img src="/logo.png" alt="Spayis logo" className="hp-logo animate-fade-in" style={{ height: '40px', width: 'auto' }} />
            <p>La solution de paiement flexible adaptée à vos besoins.</p>
            <div className="hp-social-links">
              <a href="#facebook" className="social-link-hover">f</a>
              <a href="#twitter" className="social-link-hover">𝕏</a>
              <a href="#linkedin" className="social-link-hover">in</a>
            </div>
          </div>
          <div className="hp-footer-col">
            <h5>Produit</h5>
            <ul>
              <li><a href="#features">Caractéristiques</a></li>
              <li><a href="#pricing">Tarification</a></li>
              <li><a href="#app">Application Mobile</a></li>
              <li><a href="#api">Notre API</a></li>
            </ul>
          </div>
          <div className="hp-footer-col">
            <h5>Solutions Marchands</h5>
            <ul>
              <li><a href="#ecommerce">E-commerce</a></li>
              <li><a href="#marketplace">Marketplace</a></li>
              <li><a href="#resto">Restaurants</a></li>
              <li><a href="#b2b">B2B Commerce</a></li>
            </ul>
          </div>
          <div className="hp-footer-col">
            <h5>Légal</h5>
            <ul>
              <li><a href="#confidentialite">Confidentialité</a></li>
              <li><a href="#conditions">Conditions</a></li>
              <li><a href="#cookies">Cookies</a></li>
              <li><a href="#mentions">Mentions légales</a></li>
            </ul>
          </div>
        </div>
        <div className="hp-footer-bottom">
          <p>&copy; 2026 Spayis. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
